import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { PlanDialogShell } from "../AccountSettings/PlanDialogShell";
import { CREDIT_PACKS, CreditPack, formatPrice } from "../../data/pricingPlans";
import { isNative } from "../../lib/platform";
import {
  getConsumablePricing,
  purchaseConsumable,
  StoreProductPricing,
} from "../../lib/revenueCat";
import { authClient } from "../../lib/auth-client";
import { useCreditsCheckout } from "../../hooks/useMembershipApi";
import { chatQuotaQueryKey } from "../Chat/hooks/useChatQuota";
import { ChatQuota } from "../Chat/types";

interface TopUpCreditsDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Mirrors BASE_WEEKLY_CHAT_BUDGET_USD on the API (the quota endpoint is
 * deliberately percentage-only, so the multiple is computed client-side).
 * Credits and the allowance share the same unit — raw USD of AI usage — so
 * "N× your weekly allowance" is exact arithmetic, unlike message counts.
 */
const WEEKLY_ALLOWANCE_USD = 0.2;
const USD_PER_CREDIT = 0.01;

const allowanceMultiple = (credits: number): number =>
  Math.floor((credits * USD_PER_CREDIT) / WEEKLY_ALLOWANCE_USD);

/**
 * AI credit top-up picker (Pro only — callers gate on quota.credits.canTopUp).
 * Dual purchase path behind one surface: web goes through the Lemon Squeezy
 * checkout (POST /membership/credits-checkout), native buys the matching
 * store CONSUMABLE through RevenueCat — no Lemon Squeezy surface ever renders
 * on native (App Store 3.1.1). Credits are identical wherever they're bought:
 * they land via webhook, so after a native purchase we refetch the quota
 * surfaces rather than the session.
 */
export const TopUpCreditsDialog = ({
  open,
  onClose,
}: TopUpCreditsDialogProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const queryClient = useQueryClient();
  const creditsCheckout = useCreditsCheckout();

  const [selectedSku, setSelectedSku] =
    useState<CreditPack["sku"]>("credits_10");
  const [busy, setBusy] = useState(false);
  const [purchased, setPurchased] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [storePricing, setStorePricing] = useState<
    Record<string, StoreProductPricing>
  >({});

  // Native store prices are localized per storefront and can differ from the
  // GBP stickers — the payment sheet must never contradict what we show.
  // Consumables are NOT in the subscription offerings, so they're priced by
  // direct product lookup (the same call purchaseConsumable buys through).
  useEffect(() => {
    if (!open || !isNative()) return;
    let cancelled = false;
    (async () => {
      const session = await authClient.getSession();
      const userId = session?.data?.user?.id;
      if (!userId || cancelled) return;
      try {
        const productIds = CREDIT_PACKS.map((pack) => pack.rcProductId).filter(
          (id): id is string => !!id,
        );
        const pricing = await getConsumablePricing(userId, productIds);
        if (!cancelled) setStorePricing(pricing);
      } catch {
        // Fall back to the GBP stickers.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const packPriceLabel = (pack: CreditPack): string => {
    if (isNative() && pack.rcProductId && storePricing[pack.rcProductId]) {
      return storePricing[pack.rcProductId].priceString;
    }
    return formatPrice(pack.priceGbp);
  };

  // The credits land via the RevenueCat webhook a few seconds AFTER the
  // payment sheet closes — a single invalidation usually races it and still
  // reads the old balance, leaving a blocked composer blocked. Poll briefly
  // until the balance moves (fire-and-forget: it only touches the query
  // cache, so an early dialog close is harmless).
  const pollForCredits = async () => {
    const balanceBefore =
      queryClient.getQueryData<ChatQuota>(chatQuotaQueryKey)?.credits
        ?.balanceCredits ?? 0;
    for (let attempt = 0; attempt < 10; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await queryClient.refetchQueries({ queryKey: chatQuotaQueryKey });
      const balance =
        queryClient.getQueryData<ChatQuota>(chatQuotaQueryKey)?.credits
          ?.balanceCredits ?? 0;
      if (balance > balanceBefore) return;
    }
  };

  const handleTopUp = async () => {
    const pack = CREDIT_PACKS.find(
      (candidate) => candidate.sku === selectedSku,
    );
    if (!pack) return;
    setError(null);
    setBusy(true);
    try {
      if (isNative()) {
        const session = await authClient.getSession();
        const userId = session?.data?.user?.id;
        if (!userId) {
          window.location.href = "/sign-in?reason=session_expired";
          return;
        }
        if (!pack.rcProductId) {
          throw new Error(
            "This credits pack is not available right now — please try again later",
          );
        }
        const outcome = await purchaseConsumable(userId, pack.rcProductId);
        if (outcome === "cancelled") return;
        setPurchased(true);
        queryClient.invalidateQueries({ queryKey: chatQuotaQueryKey });
        queryClient.invalidateQueries({
          queryKey: ["stockAnalysisReportQuota"],
        });
        void pollForCredits();
      } else {
        const result = await creditsCheckout.mutateAsync({ sku: pack.sku });
        window.location.href = result.data.checkoutUrl;
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Something went wrong — please try again",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    setPurchased(false);
    setError(null);
    onClose();
  };

  const primaryActionSx = {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.9375rem",
    borderRadius: 2,
    py: 1.25,
    backgroundColor: "#22c55e",
    color: "#ffffff",
    "&:hover": { backgroundColor: "#16a34a" },
    "&.Mui-disabled": {
      backgroundColor: "rgba(34, 197, 94, 0.4)",
      color: "#ffffff",
    },
  } as const;

  if (purchased) {
    return (
      <PlanDialogShell
        open={open}
        onClose={handleClose}
        title="Credits on the way"
        subtitle="Your purchase went through"
      >
        <Typography
          variant="body2"
          sx={{ textAlign: "center", color: theme.palette.text.secondary }}
        >
          The credits will appear on your balance in a moment.
        </Typography>
        <Button
          onClick={handleClose}
          fullWidth
          disableElevation
          sx={{ ...primaryActionSx, mt: 3 }}
        >
          Done
        </Button>
      </PlanDialogShell>
    );
  }

  return (
    <PlanDialogShell
      open={open}
      onClose={handleClose}
      title="Top up AI credits"
      subtitle="Spent after your weekly allowance is used. Credits roll over and last 12 months"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {CREDIT_PACKS.map((pack) => {
          const selected = pack.sku === selectedSku;
          return (
            <Box
              key={pack.sku}
              component="button"
              type="button"
              onClick={() => setSelectedSku(pack.sku)}
              aria-pressed={selected}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                width: "100%",
                px: 2,
                py: 1.5,
                borderRadius: 2,
                cursor: "pointer",
                font: "inherit",
                textAlign: "left",
                // Native <button> — the UA's default (black) text otherwise
                // leaks into the Typography children in dark mode.
                color: theme.palette.text.primary,
                backgroundColor: selected
                  ? "rgba(34, 197, 94, 0.08)"
                  : isDark
                    ? "rgba(255, 255, 255, 0.02)"
                    : "rgba(0, 0, 0, 0.02)",
                border: selected
                  ? "1px solid #22c55e"
                  : isDark
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(0, 0, 0, 0.1)",
                "&:focus-visible": {
                  outline: "2px solid #22c55e",
                  outlineOffset: 2,
                },
              }}
            >
              {/* Flat radio indicator — selection must not rely on the border alone */}
              <Box
                aria-hidden
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  flexShrink: 0,
                  border: selected
                    ? "5px solid #22c55e"
                    : `1.5px solid ${isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)"}`,
                }}
              />
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                >
                  {pack.credits} credits
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: theme.palette.text.secondary,
                    lineHeight: 1.4,
                  }}
                >
                  {allowanceMultiple(pack.credits)}× your weekly allowance
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: selected ? 600 : 500,
                  color: selected
                    ? theme.palette.text.primary
                    : theme.palette.text.secondary,
                }}
              >
                {packPriceLabel(pack)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        onClick={handleTopUp}
        disabled={busy}
        fullWidth
        disableElevation
        sx={{ ...primaryActionSx, mt: 3 }}
      >
        {busy && <CircularProgress size={18} sx={{ color: "white", mr: 1 }} />}
        {!busy && "Top up"}
      </Button>
      <Button
        onClick={handleClose}
        fullWidth
        sx={{
          mt: 1,
          textTransform: "none",
          fontWeight: 500,
          color: theme.palette.text.secondary,
        }}
      >
        Maybe later
      </Button>

      <Typography
        variant="caption"
        sx={{
          display: "block",
          textAlign: "center",
          color: theme.palette.text.secondary,
          mt: 2,
          lineHeight: 1.5,
        }}
      >
        {isNative() && "Billed securely through your app store."}
        {!isNative() && "You'll be taken to our secure checkout to finish up."}
      </Typography>
    </PlanDialogShell>
  );
};
